import { MotionMode, SongAdProject, SongAdScene, SongAdTimeline } from '../../services/api/songAdService';
import { SongAdCompositionScene, SongAdCtaCard } from './types';
import { computeSceneWindows, computeCtaWindow } from './sceneTiming';

/**
 * サーバーのシーン配列 + タイムライン（scene_order/motion）から、
 * Remotion Composition にそのまま渡せる再生用シーン列を作る純関数。
 *
 * - 並び順: timeline.scene_order を優先（詳細編集での並び替えが正）。
 *   timeline未生成（captions/init前）や、timelineに含まれない新規シーンがある場合は
 *   scenes の display_order 順を使い、取りこぼした id は末尾に足す。
 * - motion_mode: timeline.motion の編集値を優先し、無ければシーン自体の motion_mode にフォールバック
 *   （詳細編集エディタでのローカルドラフト適用にも使う。timeline編集前はサーバー値と一致する）
 * - motion_mode==='skip' のシーンは合成対象外なのでここで除外する
 * - シーン枠（startSec/endSec）: sceneTiming.computeSceneWindows で歌詞行数比配分する
 *   （backendのSongAdRenderPlanner::fromTimelineが使うProportionalTimingProviderと同一ロジック。
 *   書き出したMP4とプレビューのシーン切り替わりタイミングを一致させるための移植）
 */
export function deriveCompositionScenes(
  scenes: SongAdScene[],
  timeline: SongAdTimeline | null,
  totalSeconds: number
): SongAdCompositionScene[] {
  const byId = new Map(scenes.map((s) => [s.id, s]));

  const fallbackOrder = [...scenes].sort((a, b) => a.display_order - b.display_order).map((s) => s.id);
  const order = timeline?.scene_order?.length
    ? [...timeline.scene_order.filter((id) => byId.has(id))]
    : [...fallbackOrder];

  fallbackOrder.forEach((id) => {
    if (!order.includes(id)) order.push(id);
  });

  const included = order
    .map((id) => byId.get(id))
    .filter((s): s is SongAdScene => !!s)
    .map((s) => ({ scene: s, mode: (timeline?.motion?.[s.id]?.mode ?? s.motion_mode) as MotionMode }))
    .filter((entry) => entry.mode !== 'skip');

  const windows = computeSceneWindows(
    included.map((entry) => entry.scene.lyric_lines?.length ?? 0),
    totalSeconds
  );

  return included.map((entry, index) => ({
    id: entry.scene.id,
    motionMode: entry.mode,
    imageUrl: entry.scene.storyboard.image?.url ?? null,
    videoUrl: entry.scene.clip.video?.url ?? null,
    startSec: windows[index]?.start ?? 0,
    endSec: windows[index]?.end ?? totalSeconds,
  }));
}

/**
 * 末尾CTAカードの尺を確保し、その分だけシーン枠を後ろから縮める（v4 D/B5/F9/F11）。
 * backend `SongAdRenderPlanner::applyCta()` と同じ規則（総尺は変えず末尾シーンから尺を借りる。
 * `computeCtaWindow` が `CTA_MIN_SECONDS` 未満しか確保できないと判断したら出さない）で、
 * `deriveCompositionScenes` の出力（scenes）にCTA分のトリムを追加適用する2段目の変換。
 *
 * v4 F11: `project.cta`（backendが解決した実際の文言/ロゴURL。API契約確定）を最優先で使う。
 * `project.cta.text` が無い場合のみ、旧来の近似フォールバック（advanced_settings.cta →
 * projectタイトル）を使う。有効/無効判定も `project.cta.enabled` を優先し、
 * 無ければ互換の `project.cta_enabled` にフォールバックする。
 *
 * @returns CTA対象なら末尾を縮めたシーン配列+CTAカード情報。非対象（無効化・
 *          文言が作れない・尺の余地が無い等）なら scenes は入力のまま・cta は null
 */
export function deriveCtaCard(
  scenes: SongAdCompositionScene[],
  project: Pick<SongAdProject, 'cta_enabled' | 'cta' | 'advanced_settings' | 'title'>,
  totalSeconds: number
): { scenes: SongAdCompositionScene[]; cta: SongAdCtaCard | null } {
  const enabled = project.cta?.enabled ?? project.cta_enabled;
  if (!enabled || scenes.length === 0) {
    return { scenes, cta: null };
  }

  // 文言: project.cta.text（backendが実際に焼く文言。advanced_settings.cta → 商品名 → null を
  // backend側で解決済み）を最優先。無ければ旧来の近似フォールバック（advanced_settings.cta →
  // projectタイトル。商品名が取れないためのフロント側近似）。
  // mb_substr(text, 0, 40) 相当（日本語はBMP内でUTF-16コード単位=文字数と一致するためsliceで近似可）
  const text = (
    project.cta?.text?.trim() ||
    project.advanced_settings?.cta?.trim() ||
    project.title?.trim() ||
    ''
  ).slice(0, 40);
  if (!text) {
    return { scenes, cta: null };
  }

  const windows = scenes.map((s) => ({ start: s.startSec, end: s.endSec }));
  const trimmed = computeCtaWindow(windows, totalSeconds);
  if (!trimmed) {
    return { scenes, cta: null };
  }

  const nextScenes = scenes.map((s, i) => ({
    ...s,
    startSec: trimmed.windows[i].start,
    endSec: trimmed.windows[i].end,
  }));
  const lastScene = scenes[scenes.length - 1];

  return {
    scenes: nextScenes,
    cta: {
      start: trimmed.ctaStart,
      end: totalSeconds,
      backgroundImageUrl: lastScene.imageUrl,
      logoUrl: project.cta?.logo_url ?? null,
      text,
    },
  };
}
